package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Undo
import androidx.compose.material.icons.automirrored.filled.CallSplit
import androidx.compose.material.icons.filled.HowToReg
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.ActualWork
import com.evoapps.evoshift.domain.model.ActualWorker
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftSplits
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.isNoShow
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.usecase.shift.hasActualTime
import com.evoapps.evoshift.domain.util.plannedTimeRange
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.ui.util.personName
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoCheckboxRow
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoRadioGroup
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTimeField
import com.evoapps.evoshift.widgets.parseClockTime
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.actual_work_absence_type
import evoshift.composeapp.generated.resources.actual_work_action
import evoshift.composeapp.generated.resources.actual_work_action_description
import evoshift.composeapp.generated.resources.actual_work_as_planned
import evoshift.composeapp.generated.resources.actual_work_end
import evoshift.composeapp.generated.resources.actual_work_no_show
import evoshift.composeapp.generated.resources.actual_work_no_show_reason
import evoshift.composeapp.generated.resources.actual_work_nobody
import evoshift.composeapp.generated.resources.actual_work_person
import evoshift.composeapp.generated.resources.actual_work_person_missing
import evoshift.composeapp.generated.resources.actual_work_reason
import evoshift.composeapp.generated.resources.actual_work_reason_hint
import evoshift.composeapp.generated.resources.actual_work_record_absence
import evoshift.composeapp.generated.resources.actual_work_someone_else
import evoshift.composeapp.generated.resources.actual_work_start
import evoshift.composeapp.generated.resources.actual_work_subtitle
import evoshift.composeapp.generated.resources.actual_work_subtitle_open
import evoshift.composeapp.generated.resources.actual_work_times
import evoshift.composeapp.generated.resources.actual_work_times_from_clock
import evoshift.composeapp.generated.resources.actual_work_times_from_plan
import evoshift.composeapp.generated.resources.actual_work_times_missing
import evoshift.composeapp.generated.resources.actual_work_title
import evoshift.composeapp.generated.resources.actual_work_worked_by
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.split_action
import evoshift.composeapp.generated.resources.split_action_description
import evoshift.composeapp.generated.resources.split_handover
import evoshift.composeapp.generated.resources.split_handover_outside
import evoshift.composeapp.generated.resources.split_intro
import evoshift.composeapp.generated.resources.split_part_first
import evoshift.composeapp.generated.resources.split_part_second
import evoshift.composeapp.generated.resources.split_planned_note
import evoshift.composeapp.generated.resources.split_preview
import evoshift.composeapp.generated.resources.split_reason_hint
import evoshift.composeapp.generated.resources.split_record_sick_leave
import evoshift.composeapp.generated.resources.split_replacement
import evoshift.composeapp.generated.resources.split_save
import evoshift.composeapp.generated.resources.split_sickness
import evoshift.composeapp.generated.resources.split_title
import evoshift.composeapp.generated.resources.split_undo
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/** SH-236: test tags of the "who actually worked" and "split shift" actions, followed by the shift's id. */
const val ACTUAL_WORK_ACTION_TAG = "actual_work_action_"
const val SPLIT_ACTION_TAG = "split_action_"
const val SPLIT_UNDO_TAG = "split_undo_"
const val SPLIT_HANDOVER_TAG = "split_handover"
const val ACTUAL_WORK_START_TAG = "actual_work_start"
const val ACTUAL_WORK_END_TAG = "actual_work_end"

/** The kinds of absence a manager may record for someone who did not come. */
val NO_SHOW_ABSENCE_TYPES = listOf(
    AbsenceType.SICK_LEAVE, AbsenceType.ON_DEMAND, AbsenceType.CARE_ALLOWANCE, AbsenceType.FORCE_MAJEURE, AbsenceType.UNPAID_LEAVE
)

/** "08:00–16:00" from ISO times ("…" while the end is not known). */
internal fun clockSpan(start: String, end: String): String =
    "${start.substringAfter('T').take(5)}–${end.substringAfter('T').take(5).ifEmpty { "…" }}"

/**
 * SH-237: [uid]'s name from [nameOf] (the screen's names from ResolvePersonNamesUseCase: the team,
 * then the profiles of people from other workplaces or who left), else the "name unavailable"
 * label: never a blank or an id.
 */
@Composable
private fun nameFor(nameOf: (String) -> String?, uid: String?): String = personName(uid?.let(nameOf))

/**
 * SH-236: who worked [shift] when it was not as planned, under its times: "Actually: Ola (planned:
 * Jan)", "Nobody came (planned: Jan)" with the reason, and a split part's handover. [nameOf] names
 * a person (null: unknown, shown as the label, SH-237); nothing when it went as planned.
 */
@Composable
fun ActualWorkLines(shift: Shift, nameOf: (String) -> String?, color: Color = Color.White.copy(alpha = 0.75f)) {
    val style = MaterialTheme.typography.bodySmall
    val planned = shift.userId?.let { nameFor(nameOf, it) }
    val actual = shift.actualUserId
    when {
        shift.isNoShow -> {
            Text(stringResource(Res.string.actual_work_no_show, nameFor(nameOf, shift.userId)), style = style, color = LightGold)
            shift.noShowReason?.let { Text(stringResource(Res.string.actual_work_no_show_reason, it), style = style, color = color) }
        }
        actual != null && actual != shift.userId && planned != null ->
            Text(stringResource(Res.string.actual_work_worked_by, nameFor(nameOf, actual), planned), style = style, color = LightGold)
    }
    shift.split?.let { split ->
        val at = split.handoverAt.substringAfter('T').take(5)
        val other = nameFor(nameOf, split.otherUserId)
        Text(
            stringResource(if (split.isFirst) Res.string.split_part_first else Res.string.split_part_second, at, other),
            style = style, color = color
        )
    }
}

/**
 * SH-236: the actions on one shift of a past day or today: "Who worked…", "Split shift…" (a
 * shift someone works, not split yet) and "Undo split" (a split part). Null actions are not offered.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ActualWorkActions(
    shift: Shift,
    onActualWork: (() -> Unit)?,
    onSplit: (() -> Unit)?,
    onUndoSplit: (() -> Unit)?,
    color: Color = LightGold
) {
    val times = clockSpan(shift.plannedTime.start, shift.plannedTime.end)
    FlowRow(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
        onActualWork?.let { open ->
            val description = stringResource(Res.string.actual_work_action_description, times)
            ActionButton(stringResource(Res.string.actual_work_action), Icons.Default.HowToReg, color, ACTUAL_WORK_ACTION_TAG + shift.id, description, open)
        }
        if (shift.split == null && shift.workerId != null) onSplit?.let { open ->
            val description = stringResource(Res.string.split_action_description, times)
            ActionButton(stringResource(Res.string.split_action), Icons.AutoMirrored.Filled.CallSplit, color, SPLIT_ACTION_TAG + shift.id, description, open)
        }
        if (shift.split != null) onUndoSplit?.let { undo ->
            ActionButton(stringResource(Res.string.split_undo), Icons.AutoMirrored.Filled.Undo, color, SPLIT_UNDO_TAG + shift.id, null, undo)
        }
    }
}

@Composable
private fun ActionButton(label: String, icon: androidx.compose.ui.graphics.vector.ImageVector, color: Color, tag: String, description: String?, onClick: () -> Unit) {
    TextButton(
        onClick = onClick,
        colors = ButtonDefaults.textButtonColors(contentColor = color),
        modifier = Modifier.handCursor().testTag(tag).let { m -> description?.let { d -> m.semantics { contentDescription = d } } ?: m }
    ) {
        Icon(icon, contentDescription = null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(6.dp))
        Text(label)
    }
}

private enum class WorkerChoice { AS_PLANNED, SOMEONE_ELSE, NOBODY }

/**
 * SH-236: "Who actually worked?" for one shift: as planned (the planned person, the default),
 * someone else from [staff], or nobody came (an optional reason, and the planned person's absence
 * that day), with the actual times: the clocked ones when there are, else the planned ones.
 */
@Composable
fun ActualWorkDialog(
    shift: Shift,
    staff: List<User>,
    nameOf: (String) -> String?,
    onDismiss: () -> Unit,
    onSave: (worker: ActualWorker, actual: TimeRange?, absenceType: AbsenceType?) -> Unit
) {
    val planned = shift.userId
    var choice by rememberSaveable {
        mutableStateOf(
            when {
                shift.notWorked -> WorkerChoice.NOBODY
                planned == null || (shift.actualUserId != null && shift.actualUserId != planned) -> WorkerChoice.SOMEONE_ELSE
                else -> WorkerChoice.AS_PLANNED
            }
        )
    }
    var person by rememberSaveable { mutableStateOf(shift.actualUserId?.takeIf { it != planned }) }
    val clocked = shift.hasActualTime && shift.actualTime?.end?.isNotEmpty() == true && shift.actualTime?.start?.isNotEmpty() == true
    val source = if (clocked) shift.actualTime!! else shift.plannedTime
    var start by rememberSaveable { mutableStateOf(source.start.substringAfter('T').take(5)) }
    var end by rememberSaveable { mutableStateOf(source.end.substringAfter('T').take(5)) }
    var reason by rememberSaveable { mutableStateOf(shift.noShowReason.orEmpty()) }
    var recordAbsence by rememberSaveable { mutableStateOf(false) }
    var absenceType by remember { mutableStateOf(AbsenceType.SICK_LEAVE) }
    var showErrors by remember { mutableStateOf(false) }

    val date = remember(shift.date) { runCatching { LocalDate.parse(shift.date) }.getOrNull() }
    val startClock = parseClockTime(start)
    val endClock = parseClockTime(end)
    val times = if (date != null && startClock != null && endClock != null) runCatching { plannedTimeRange(date, startClock, endClock) }.getOrNull() else null
    val worker: ActualWorker? = when (choice) {
        WorkerChoice.AS_PLANNED -> ActualWorker.AsPlanned
        WorkerChoice.SOMEONE_ELSE -> person?.let { ActualWorker.Person(it) }
        WorkerChoice.NOBODY -> ActualWorker.Nobody(reason.trim().ifEmpty { null })
    }
    val valid = worker != null && (choice == WorkerChoice.NOBODY || times != null)
    val span = clockSpan(shift.plannedTime.start, shift.plannedTime.end)
    val options = buildList {
        if (planned != null) add(WorkerChoice.AS_PLANNED)
        add(WorkerChoice.SOMEONE_ELSE)
        add(WorkerChoice.NOBODY)
    }

    EvoDialog(
        title = stringResource(Res.string.actual_work_title),
        subtitle = if (planned != null) stringResource(Res.string.actual_work_subtitle, span, nameFor(nameOf, planned))
        else stringResource(Res.string.actual_work_subtitle_open, span),
        icon = Icons.Default.HowToReg,
        onDismiss = onDismiss,
        dismissOnClickOutside = false,
        confirm = DialogAction(
            stringResource(Res.string.save),
            {
                showErrors = true
                if (valid) onSave(worker!!, times.takeIf { choice != WorkerChoice.NOBODY }, absenceType.takeIf { choice == WorkerChoice.NOBODY && recordAbsence && planned != null })
            },
            enabled = valid || !showErrors
        )
    ) {
        EvoRadioGroup(
            options = options,
            selected = choice,
            onSelect = { choice = it },
            optionLabel = { option ->
                when (option) {
                    WorkerChoice.AS_PLANNED -> stringResource(Res.string.actual_work_as_planned, nameFor(nameOf, planned))
                    WorkerChoice.SOMEONE_ELSE -> stringResource(Res.string.actual_work_someone_else)
                    WorkerChoice.NOBODY -> stringResource(Res.string.actual_work_nobody)
                }
            }
        )
        if (choice == WorkerChoice.SOMEONE_ELSE) {
            val people = remember(staff, planned) { staff.filter { it.uid != planned }.sortedBy { it.displayName.lowercase() }.map { it.uid } }
            EvoDropdown(
                label = stringResource(Res.string.actual_work_person),
                options = people,
                selected = person,
                onSelected = { person = it },
                optionLabel = { nameFor(nameOf, it) },
                error = if (showErrors && person == null) stringResource(Res.string.actual_work_person_missing) else null
            )
        }
        if (choice == WorkerChoice.NOBODY) {
            EvoTextField(
                value = reason,
                onValueChange = { reason = it.take(ActualWork.MAX_NOTE) },
                label = stringResource(Res.string.actual_work_reason),
                placeholder = stringResource(Res.string.actual_work_reason_hint)
            )
            if (planned != null) {
                EvoCheckboxRow(
                    checked = recordAbsence,
                    onCheckedChange = { recordAbsence = it },
                    label = stringResource(Res.string.actual_work_record_absence, nameFor(nameOf, planned))
                )
                if (recordAbsence) {
                    EvoDropdown(
                        label = stringResource(Res.string.actual_work_absence_type),
                        options = NO_SHOW_ABSENCE_TYPES,
                        selected = absenceType,
                        onSelected = { absenceType = it },
                        optionLabel = { it.label() }
                    )
                }
            }
        } else {
            DialogSection(stringResource(Res.string.actual_work_times)) {
                Text(
                    stringResource(if (clocked) Res.string.actual_work_times_from_clock else Res.string.actual_work_times_from_plan),
                    style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.7f)
                )
                Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
                    EvoTimeField(start, { start = it }, stringResource(Res.string.actual_work_start), Modifier.weight(1f).testTag(ACTUAL_WORK_START_TAG), showErrors = showErrors)
                    EvoTimeField(end, { end = it }, stringResource(Res.string.actual_work_end), Modifier.weight(1f).testTag(ACTUAL_WORK_END_TAG), showErrors = showErrors)
                }
                if (showErrors && times == null) {
                    Text(stringResource(Res.string.actual_work_times_missing), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                }
            }
        }
    }
}

/** What "Split shift" sends: the handover (local ISO), the colleague, the note and the illness. */
data class SplitRequest(
    val handoverAt: String,
    val replacementId: String,
    val reason: String?,
    val sickness: Boolean,
    val recordSickLeave: Boolean
)

/**
 * SH-236: "Split shift": the handover time (inside the shift: after its start and the clock-in,
 * before its end and the clock-out), who takes over from [staff], an optional note and whether it
 * is because of illness (then the sick leave for the rest of the day may be recorded). A preview
 * shows both parts. A day after [today] is a planned split (it waits for publishing).
 */
@Composable
fun SplitShiftDialog(
    shift: Shift,
    staff: List<User>,
    nameOf: (String) -> String?,
    today: String?,
    onDismiss: () -> Unit,
    onSplit: (SplitRequest) -> Unit
) {
    val worker = shift.workerId
    var handover by rememberSaveable { mutableStateOf("") }
    var replacement by rememberSaveable { mutableStateOf<String?>(null) }
    var reason by rememberSaveable { mutableStateOf("") }
    var sickness by rememberSaveable { mutableStateOf(false) }
    var recordSickLeave by rememberSaveable { mutableStateOf(false) }
    var showErrors by remember { mutableStateOf(false) }

    val clock = parseClockTime(handover)
    val at = clock?.let { ShiftSplits.handoverAt(shift, it) }
    val inside = at != null && ShiftSplits.isInside(shift, at)
    val valid = inside && replacement != null && replacement != worker
    val lower = listOfNotNull(shift.plannedTime.start, shift.actualTime?.start?.takeIf { it.isNotEmpty() }).max()
    val upper = listOfNotNull(shift.plannedTime.end, shift.actualTime?.end?.takeIf { it.isNotEmpty() }).min()
    val outsideText = stringResource(Res.string.split_handover_outside, lower.substringAfter('T').take(5), upper.substringAfter('T').take(5))
    val workerName = nameFor(nameOf, worker)

    EvoDialog(
        title = stringResource(Res.string.split_title),
        subtitle = "$workerName · ${clockSpan(shift.plannedTime.start, shift.plannedTime.end)}",
        icon = Icons.AutoMirrored.Filled.CallSplit,
        onDismiss = onDismiss,
        dismissOnClickOutside = false,
        confirm = DialogAction(
            stringResource(Res.string.split_save),
            {
                showErrors = true
                if (valid) onSplit(SplitRequest(at!!, replacement!!, reason.trim().ifEmpty { null }, sickness, sickness && recordSickLeave))
            },
            enabled = valid || !showErrors
        )
    ) {
        Text(stringResource(Res.string.split_intro), style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.8f))
        if (today != null && shift.date > today) {
            Text(stringResource(Res.string.split_planned_note), style = MaterialTheme.typography.bodySmall, color = LightGold)
        }
        EvoTimeField(
            value = handover,
            onValueChange = { handover = it },
            label = stringResource(Res.string.split_handover),
            modifier = Modifier.testTag(SPLIT_HANDOVER_TAG),
            showErrors = showErrors,
            error = outsideText.takeIf { clock != null && !inside }
        )
        val people = remember(staff, worker) { staff.filter { it.uid != worker }.sortedBy { it.displayName.lowercase() }.map { it.uid } }
        EvoDropdown(
            label = stringResource(Res.string.split_replacement),
            options = people,
            selected = replacement,
            onSelected = { replacement = it },
            optionLabel = { nameFor(nameOf, it) },
            error = if (showErrors && replacement == null) stringResource(Res.string.actual_work_person_missing) else null
        )
        EvoTextField(
            value = reason,
            onValueChange = { reason = it.take(ActualWork.MAX_NOTE) },
            label = stringResource(Res.string.actual_work_reason),
            placeholder = stringResource(Res.string.split_reason_hint)
        )
        EvoCheckboxRow(checked = sickness, onCheckedChange = { sickness = it }, label = stringResource(Res.string.split_sickness))
        if (sickness && worker != null) {
            EvoCheckboxRow(
                checked = recordSickLeave,
                onCheckedChange = { recordSickLeave = it },
                label = stringResource(Res.string.split_record_sick_leave, workerName, clock ?: "…")
            )
        }
        if (inside && replacement != null) {
            Text(
                stringResource(
                    Res.string.split_preview,
                    workerName, clockSpan(shift.plannedTime.start, at!!),
                    nameFor(nameOf, replacement), clockSpan(at, shift.plannedTime.end)
                ),
                style = MaterialTheme.typography.bodyMedium, color = LightGold
            )
        }
    }
}
